import { useRef, useState } from "react";
import type { CSSProperties } from "react";
import { useParams } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  createFileUploadSession,
  uploadFilePart,
  completeFileUpload,
  listFormUploads,
  deleteFormUpload,
} from "../lib/api-client";

function FormFileManagementPage(): JSX.Element {
  const { formId } = useParams<{ teamId: string; formId: string }>();
  const queryClient = useQueryClient();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [dragActive, setDragActive] = useState(false);
  const [uploadingFiles, setUploadingFiles] = useState<string[]>([]);
  const [toast, setToast] = useState<{ type: "success" | "error"; message: string } | null>(null);

  // Fetch files from backend
  const uploadsQuery = useQuery({
    queryKey: ["form-uploads", formId],
    queryFn: () => listFormUploads(formId!),
    enabled: Boolean(formId),
  });

  const files = uploadsQuery.data?.uploads ?? [];

  // Upload mutation
  const uploadMutation = useMutation({
    mutationFn: async (file: File) => {
      if (!formId) throw new Error("formId missing");

      // 1. Create upload session
      const session = await createFileUploadSession(formId, {
        filename: file.name,
        contentType: file.type || "application/octet-stream",
        sizeBytes: file.size,
      });

      // 2. Read file and upload as single part (simplified - works for smaller files)
      const arrayBuffer = await file.arrayBuffer();
      const base64Data = btoa(
        new Uint8Array(arrayBuffer).reduce((data, byte) => data + String.fromCharCode(byte), "")
      );

      const partResult = await uploadFilePart(formId, session.uploadId, 1, {
        key: session.key,
        data: base64Data,
      });

      // 3. Complete upload with metadata
      await completeFileUpload(formId, session.uploadId, {
        key: session.key,
        filename: file.name,
        contentType: file.type || "application/octet-stream",
        sizeBytes: file.size,
        parts: [partResult],
      });

      return file.name;
    },
    onMutate(file) {
      setUploadingFiles((prev) => [...prev, file.name]);
    },
    onSuccess(filename) {
      setUploadingFiles((prev) => prev.filter((f) => f !== filename));
      void queryClient.invalidateQueries({ queryKey: ["form-uploads", formId] });
      setToast({ type: "success", message: `${filename} uploaded successfully.` });
      setTimeout(() => setToast(null), 3000);
    },
    onError(error: unknown, file) {
      setUploadingFiles((prev) => prev.filter((f) => f !== file.name));
      setToast({
        type: "error",
        message: error instanceof Error ? error.message : "Failed to upload file.",
      });
    },
  });

  // Delete mutation
  const deleteMutation = useMutation({
    mutationFn: async (uploadId: string) => {
      if (!formId) throw new Error("formId missing");
      return deleteFormUpload(formId, uploadId);
    },
    onSuccess() {
      void queryClient.invalidateQueries({ queryKey: ["form-uploads", formId] });
      setToast({ type: "success", message: "File deleted." });
      setTimeout(() => setToast(null), 3000);
    },
    onError(error: unknown) {
      setToast({
        type: "error",
        message: error instanceof Error ? error.message : "Failed to delete file.",
      });
    },
  });

  const handleDrag = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === "dragenter" || e.type === "dragover") {
      setDragActive(true);
    } else if (e.type === "dragleave") {
      setDragActive(false);
    }
  };

  const handleDrop = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);

    const droppedFiles = e.dataTransfer.files;
    if (droppedFiles.length > 0) {
      Array.from(droppedFiles).forEach((file) => {
        uploadMutation.mutate(file);
      });
    }
  };

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const selectedFiles = e.currentTarget.files;
    if (selectedFiles && selectedFiles.length > 0) {
      Array.from(selectedFiles).forEach((file) => {
        uploadMutation.mutate(file);
      });
    }
    // Reset input so same file can be selected again
    e.currentTarget.value = "";
  };

  const handleDeleteFile = (uploadId: string) => {
    if (confirm("Are you sure you want to delete this file?")) {
      deleteMutation.mutate(uploadId);
    }
  };

  const formatFileSize = (bytes: number): string => {
    if (bytes === 0) return "0 Bytes";
    const k = 1024;
    const sizes = ["Bytes", "KB", "MB", "GB"];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return Math.round((bytes / Math.pow(k, i)) * 100) / 100 + " " + sizes[i];
  };

  const containerStyle: CSSProperties = {
    maxWidth: "1200px",
    margin: "0 auto",
    padding: "2rem",
  };

  const cardStyle: CSSProperties = {
    background: "var(--color-surface)",
    border: "1px solid var(--color-border)",
    borderRadius: "var(--radius-base)",
    padding: "1.5rem",
    marginBottom: "2rem",
  };

  const dropZoneStyle: CSSProperties = {
    border: "2px dashed var(--color-border)",
    borderRadius: "var(--radius-base)",
    padding: "2rem",
    textAlign: "center",
    cursor: "pointer",
    transition: "all 0.15s",
    background: dragActive ? "var(--color-primary-soft)" : "var(--color-bg-solid)",
    borderColor: dragActive ? "var(--color-primary)" : "var(--color-border)",
  };

  const fileItemStyle: CSSProperties = {
    background: "var(--color-bg-solid)",
    border: "1px solid var(--color-border)",
    borderRadius: "var(--radius-base)",
    padding: "1rem",
    marginBottom: "0.75rem",
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: "1rem",
  };

  return (
    <div style={containerStyle}>
      <h1 style={{ fontSize: "1.75rem", fontWeight: 600, marginBottom: "2rem" }}>
        File Management
      </h1>

      {/* Upload Section */}
      <div style={cardStyle}>
        <h2 style={{ fontSize: "1.125rem", fontWeight: 600, marginBottom: "1.5rem" }}>
          Upload Files
        </h2>

        <div
          style={dropZoneStyle}
          onDragEnter={handleDrag}
          onDragLeave={handleDrag}
          onDragOver={handleDrag}
          onDrop={handleDrop}
          onClick={() => fileInputRef.current?.click()}
        >
          <input
            ref={fileInputRef}
            type="file"
            multiple
            onChange={handleFileSelect}
            style={{ display: "none" }}
          />
          <div style={{ fontSize: "2rem", marginBottom: "0.5rem" }}>📁</div>
          <p style={{ fontWeight: 500, marginBottom: "0.25rem" }}>
            Drag and drop files here or click to select
          </p>
          <p style={{ fontSize: "0.875rem", color: "var(--color-text-muted)" }}>
            Supported: Images, Documents, Videos (Max 50MB per file)
          </p>
        </div>

        {/* Uploading files indicator */}
        {uploadingFiles.length > 0 && (
          <div style={{ marginTop: "1rem", color: "var(--color-text-muted)" }}>
            Uploading: {uploadingFiles.join(", ")}...
          </div>
        )}
      </div>

      {/* Files List */}
      <div style={cardStyle}>
        <h2 style={{ fontSize: "1.125rem", fontWeight: 600, marginBottom: "1.5rem" }}>
          Uploaded Files ({files.length})
        </h2>

        {uploadsQuery.isLoading ? (
          <p style={{ color: "var(--color-text-muted)" }}>Loading files...</p>
        ) : files.length === 0 ? (
          <p style={{ color: "var(--color-text-muted)" }}>No files uploaded yet.</p>
        ) : (
          <div>
            {files.map((file) => {
              const fileUrl = `https://assets-forms.rwa.shiksha/${file.r2Key}`;
              return (
                <div key={file.id} style={fileItemStyle}>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontWeight: 500, marginBottom: "0.25rem" }}>
                      {file.filename}
                    </div>
                    <div style={{ fontSize: "0.75rem", color: "var(--color-text-muted)" }}>
                      {formatFileSize(file.sizeBytes)} • {new Date(file.createdAt).toLocaleString()}
                      {file.uploaderName && <> • Uploaded by: {file.uploaderName}</>}
                    </div>
                  </div>
                  <div style={{ display: "flex", gap: "0.5rem", flexShrink: 0 }}>
                    <button
                      type="button"
                      className="button ghost"
                      onClick={() => {
                        void navigator.clipboard.writeText(fileUrl);
                        setToast({ type: "success", message: "URL copied to clipboard." });
                        setTimeout(() => setToast(null), 2000);
                      }}
                      style={{ fontSize: "0.875rem" }}
                    >
                      Copy Url
                    </button>
                    <button
                      type="button"
                      className="button ghost"
                      onClick={() => handleDeleteFile(file.id)}
                      style={{ fontSize: "0.875rem", color: "var(--color-critical)" }}
                      disabled={deleteMutation.isPending}
                    >
                      Delete
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* File Usage Info */}
      <div style={cardStyle}>
        <h2 style={{ fontSize: "1.125rem", fontWeight: 600, marginBottom: "1rem" }}>
          File Usage
        </h2>
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "1rem" }}>
          <div>
            <div style={{ fontSize: "0.875rem", color: "var(--color-text-muted)", marginBottom: "0.25rem" }}>
              Total Files
            </div>
            <div style={{ fontSize: "1.5rem", fontWeight: 600 }}>
              {files.length}
            </div>
          </div>
          <div>
            <div style={{ fontSize: "0.875rem", color: "var(--color-text-muted)", marginBottom: "0.25rem" }}>
              Total Size
            </div>
            <div style={{ fontSize: "1.5rem", fontWeight: 600 }}>
              {formatFileSize(files.reduce((sum, f) => sum + f.sizeBytes, 0))}
            </div>
          </div>
        </div>
      </div>

      {/* Toast Notification */}
      {toast && (
        <div
          style={{
            position: "fixed",
            bottom: "1.5rem",
            right: "1.5rem",
            background: toast.type === "success" ? "var(--color-success)" : "var(--color-critical)",
            color: "white",
            padding: "1rem 1.5rem",
            borderRadius: "var(--radius-base)",
            boxShadow: "var(--shadow-lg)",
            zIndex: 1001,
          }}
        >
          {toast.message}
        </div>
      )}
    </div>
  );
}

export default FormFileManagementPage;

